import React, { useEffect, useState } from "react";
import { FaVideo, FaRegFileAlt, FaStar, FaTimes } from "react-icons/fa";
import { Link } from "react-router-dom";
import { useAppSelector } from "@/hooks/hook";
import { GET } from "@/services/api";
import { API_ROUTES } from "@/services/environment";
import { ApiResource, ResourceType } from "@/pages/Resources/interface";
import ResourceCard from "@/pages/Resources/components/ResourceCard";
import { Skeleton } from "@/components/skeleton/Skeleton";

const RECENT_LIMIT = 3;

const ResourceCardSkeleton = () => (
    <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
        <Skeleton className="aspect-[16/10] rounded-none" />
        <div className="p-3 flex flex-col gap-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
        </div>
    </div>
);

const tabIcon: Record<ResourceType, React.ReactNode> = {
    Video: <FaVideo />,
    Document: <FaRegFileAlt />,
};

export const ResourceCenter = () => {
    const { roles } = useAppSelector((store) => store.user);
    const isAdmin = roles === "admin";
    const { listResourcesApi } = API_ROUTES;

    const [activeFilter, setActiveFilter] = useState<ResourceType>("Video");
    const [activeVideo, setActiveVideo] = useState<string | null>(null);
    const [resources, setResources] = useState<ApiResource[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [tabCounts, setTabCounts] = useState<Record<ResourceType, number>>({ Video: 0, Document: 0 });

    const tabs: ResourceType[] = ["Video", "Document"];

    useEffect(() => {
        (async () => {
            try {
                setIsLoading(true);
                const query = new URLSearchParams({
                    type: activeFilter,
                    limit: String(RECENT_LIMIT),
                    pageNumber: "1",
                });
                if (!isAdmin) query.set("is_active", "true");
                const result = await GET(`${listResourcesApi}?${query.toString()}`);
                setResources(result?.data?.result ?? []);
            } catch (error) {
                console.log("get resources error:", error);
            } finally {
                setIsLoading(false);
            }
        })();
    }, [listResourcesApi, activeFilter, isAdmin]);

    useEffect(() => {
        (async () => {
            try {
                const activeParam = isAdmin ? "" : "&is_active=true";
                const [videoResult, documentResult] = await Promise.all([
                    GET(`${listResourcesApi}?type=Video&limit=1&pageNumber=1${activeParam}`),
                    GET(`${listResourcesApi}?type=Document&limit=1&pageNumber=1${activeParam}`),
                ]);
                setTabCounts({
                    Video: videoResult?.data?.totalResults ?? 0,
                    Document: documentResult?.data?.totalResults ?? 0,
                });
            } catch (error) {
                console.log("get resource counts error:", error);
            }
        })();
    }, [listResourcesApi, isAdmin]);

    return (
        <div className="bg-white rounded-2xl p-6 h-full relative">
            <div className="flex justify-between items-start mb-5 flex-wrap gap-2">
                <p className="text-xl font-semibold">Resource Center</p>
                <Link
                    to="/resources"
                    className="bg-black text-white rounded-full py-2 px-5 text-sm flex items-center gap-2 whitespace-nowrap"
                >
                    View All
                </Link>
            </div>

            <div className="flex gap-2 flex-wrap mb-5">
                {tabs.map((tab) => (
                    <button
                        key={tab}
                        type="button"
                        onClick={() => setActiveFilter(tab)}
                        className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm border transition-colors ${activeFilter === tab
                            ? "bg-primary-red text-white border-primary-red"
                            : "border-gray-200 text-gray-600"
                            }`}
                    >
                        {tabIcon[tab]}
                        {tab}s <span className="opacity-70">{tabCounts[tab]}</span>
                    </button>
                ))}
            </div>

            <p className="flex items-center gap-2 text-xs font-bold text-primary-red uppercase tracking-wide mb-3">
                <FaStar size={12} /> Recently Added
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {isLoading ? (
                    Array.from({ length: RECENT_LIMIT }).map((_, i) => <ResourceCardSkeleton key={i} />)
                ) : resources.length > 0 ? (
                    resources.map((resource) => (
                        <ResourceCard key={resource.id} resource={resource} onPlay={setActiveVideo} />
                    ))
                ) : (
                    <p className="text-center text-gray-500 text-sm py-4 col-span-full">
                        No resources found
                    </p>
                )}
            </div>

            {activeVideo && (
                <div
                    className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6"
                    onClick={() => setActiveVideo(null)}
                >
                    <div
                        className="relative w-full max-w-3xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            className="absolute -top-9 right-0 w-8 h-8 rounded-full bg-white/20 text-white flex items-center justify-center"
                            onClick={() => setActiveVideo(null)}
                        >
                            <FaTimes size={14} />
                        </button>
                        <div className="aspect-video rounded-xl overflow-hidden bg-black">
                            <iframe
                                className="w-full h-full"
                                src={`https://www.youtube.com/embed/${activeVideo}?autoplay=1&rel=0`}
                                title="Video player"
                                allow="autoplay; encrypted-media"
                                allowFullScreen
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
